import { toRefs, watch } from 'vue';
import { type I18n, type Composer, type VueI18n, type TranslateOptions } from 'vue-i18n';

import { useCookies } from '@bv/composables';
import { localStorageConsts } from '@bv/constants/localStorage';
import { getLangByCode, nextLanguage, APP_TITLE_TRANS } from '@bv/defaults/i18n';
import { getInitialLanguage } from '@bv/defaults/i18n';
import { useBrandStore } from '@bv/stores';
import type { LanguageCode, LanguageConfig } from '@bv/types/plugins/i18n';

let globalI18n: I18n;

type TransFunction = {
  /**
   * Translates a given key.
   * @param {string} key - The translation key.
   * @returns {string} The translated string.
   */
  (key: string): string;
  /**
   * Translates a key with named interpolation.
   * @param {string} key - The translation key (e.g., 'welcome {name}').
   * @param {Record<string, unknown>} named - An object of named values.
   * @returns {string} The translated string.
   */
  (key: string, named: Record<string, unknown>): string;
  /**
   * Translates a key with list interpolation.
   * @param {string} key - The translation key (e.g., 'hello {0}').
   * @param {unknown[]} list - An array of values.
   * @returns {string} The translated string.
   */
  (key: string, list: unknown[]): string;
  /**
   * Translates a key with pluralization.
   * @param {string} key - The translation key (e.g., 'no apples | one apple | {count} apples').
   * @param {number} plural - The number for pluralization.
   * @returns {string} The translated string.
   */
  (key: string, plural: number): string;
  /**
   * Translates a key with a full options object.
   * @param {string} key - The translation key.
   * @param {TranslateOptions} options - A full `vue-i18n` options object.
   * @returns {string} The translated string.
   */
  (key: string, options: TranslateOptions): string;
};

/**
 * Sets the global i18n instance to be used by other functions
 *
 * @param {I18n} instance - The i18n instance (either Vue I18n v9 Composer or legacy VueI18n instance)
 * @returns {void}
 *
 * @example
 * const i18n = createI18n({ locale: 'en', messages: {} });
 * setI18nInstance(i18n);
 */
export const setI18nInstance = (instance: I18n): void => {
  globalI18n = instance;
};

/**
 * Translates a localization key
 *
 * @param {string} key - Translation key
 * @param {...any[]} args - Additional arguments for the `t` function.
 * @returns {string} Translated string
 * @throws {Error} If i18n instance is not initialized
 *
 * @example
 * const message = trans('hello.world');
 */
export const trans: TransFunction = (key: string, ...args: any[]): string => {
  if (!globalI18n) {
    throw new Error('i18n instance not initialized! Call setI18nInstance() first');
  }

  if ('global' in globalI18n) {
    const composer = globalI18n.global as Composer;
    return (composer.t as any)(key, ...args);
  } else {
    const legacyI18n = globalI18n as unknown as VueI18n;
    return (legacyI18n.t as any)(key, ...args);
  }
};

/**
 * Switches the application's current language and updates all related configurations.
 * This includes the i18n locale, document title, HTML attributes, and persists the preference in localStorage.
 *
 * @param {LanguageCode|undefined} [lang=undefined] - Optional language code to switch to (e.g., 'en', 'ar').
 *                                                    If not provided, the next language in the cycle will be used automatically.
 *
 * @param {boolean} [saveToCookies=true] - Optional save language to cookies.
 * @returns {void} - This function does not return anything; it performs side effects only.
 *
 * @sideEffects
 * - Updates the i18n.js/vue-i18n locale globally.
 * - Changes the document title to the translated version of `APP_TITLE_TRANS`.
 * - Sets the `lang` and `dir` (RTL/LTR) attributes on the HTML element.
 * - Saves the selected language in cookies for persistence.
 */
export const switchLanguage = (
  lang?: LanguageCode,
  saveToCookies: boolean = true,
  updateTitle: boolean = true,
): void => {
  const newLanguage: LanguageCode = lang || nextLanguage(getLocaleCode() as LanguageCode).code;

  setLocale(newLanguage);

  if (updateTitle) {
    const { config } = toRefs(useBrandStore());

    const setTitle = (): void => {
      document.title = config.value?.isDefault === false ? config.value.name[newLanguage] : trans(APP_TITLE_TRANS);
    };

    setTitle();

    watch(() => config.value, setTitle, { deep: true });
  }
  document.documentElement.setAttribute('lang', newLanguage);
  document.documentElement.setAttribute('dir', getLangByCode(newLanguage)?.isRtl ? 'rtl' : 'ltr');

  if (saveToCookies) {
    const { set, config } = useCookies();
    config();
    set(localStorageConsts.language, newLanguage, { expires: '1y' });
  }
};

/**
 * Gets current locale code
 *
 * @returns {string} Current locale code (e.g. 'en', 'ar').
 *                   Or return fallback local if i18n instance is not initialized
 *
 * @example
 * const currentLocaleCode = getLocaleCode();
 */
export const getLocaleCode = (): string => {
  if (!globalI18n) {
    return getInitialLanguage().code;
  }

  if (typeof globalI18n.global.locale !== 'string') {
    return globalI18n.global.locale.value;
  } else {
    return globalI18n.global.locale;
  }
};

/**
 * Gets current locale
 *
 * @returns {string} Current locale.
 *                   Or return fallback local if i18n instance is not initialized
 *
 * @example
 * const currentLocale = getLocale();
 */
export const getLocale = (): LanguageConfig => {
  let localeCode: LanguageCode;
  if (!globalI18n) {
    localeCode = getInitialLanguage().code;
  }

  if (typeof globalI18n.global.locale !== 'string') {
    localeCode = globalI18n.global.locale.value as LanguageCode;
  } else {
    localeCode = globalI18n.global.locale as LanguageCode;
  }

  return getLangByCode(localeCode) as LanguageConfig;
};

/**
 * Sets new locale
 *
 * @param {string} locale - Locale code to set (e.g. 'en', 'ar')
 * @returns {void}
 * @throws {Error} If i18n instance is not initialized
 *
 * @example
 * setLocale('ar');
 */
export const setLocale = (locale: string): void => {
  if (!globalI18n) {
    throw new Error('i18n instance not initialized!');
  }

  if (typeof globalI18n.global.locale !== 'string') {
    globalI18n.global.locale.value = locale;
  } else {
    globalI18n.global.locale = locale;
  }
};
